/**
 * USERS DATA - API REAL
 * =====================
 * Funções para gerenciar dados de usuários via API
 */

import { apiClient } from './api-client'
import type { User } from "./user-types"

// Função para configurar a tabela users
export async function setupUsersTable(): Promise<void> {
  try {
    const response = await fetch("/api/users/setup", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    })
    
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `Erro HTTP: ${response.status}`)
    }
    
    const result = await response.json()
  } catch (error) {
    console.error("❌ [USERS] Erro ao configurar tabela:", error)
    throw error
  }
}

export async function buscarUsuarios(): Promise<User[]> {
  try {
    const usersData = await apiClient.get('/api/users')
    // Mapear dados do Supabase para o formato esperado
    const users = usersData.map((user: any) => ({
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      active: user.active,
      createdAt: user.created_at // Mapear created_at para createdAt
    }))
    return users
  } catch (error) {
    console.error("❌ [USERS] Erro ao buscar usuários:", error)
    throw error
  }
}

export async function buscarUsuarioPorEmail(email: string): Promise<User | null> {
  try {
    const users = await buscarUsuarios()
    const usuario = users.find(u => u.email === email && u.active)
    
    if (usuario) {
      return usuario
    } else {
      return null
    }
  } catch (error) {
    console.error("❌ [USERS] Erro ao buscar usuário por email:", error)
    throw error
  }
}

export async function salvarUsuario(usuario: Omit<User, "id" | "createdAt">): Promise<User> {
  try {
    const response = await fetch("/api/users", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(usuario),
    })
    
    if (!response.ok) {
      // Se a tabela não existe, tentar criar
      if (response.status === 500) {
        await setupUsersTable()
        
        // Tentar novamente
        const retryResponse = await fetch("/api/users", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(usuario),
        })
        
        if (!retryResponse.ok) {
          const errorData = await retryResponse.json()
          throw new Error(errorData.error || `Erro HTTP: ${retryResponse.status}`)
        }
        
        const userData = await retryResponse.json()
        // Mapear dados do Supabase para o formato esperado
        const novoUsuario = {
          id: userData.id,
          name: userData.name,
          email: userData.email,
          role: userData.role,
          active: userData.active,
          createdAt: userData.created_at
        }
        
        return novoUsuario
      }
      
      const errorData = await response.json()
      throw new Error(errorData.error || `Erro HTTP: ${response.status}`)
    }
    
    const userData = await response.json()
    // Mapear dados do Supabase para o formato esperado
    const novoUsuario = {
      id: userData.id,
      name: userData.name,
      email: userData.email,
      role: userData.role,
      active: userData.active,
      createdAt: userData.created_at
    }
    
    return novoUsuario
  } catch (error) {
    console.error("❌ [USERS] Erro ao salvar usuário:", error)
    throw error
  }
}

export async function atualizarUsuario(id: string, dados: Partial<User>): Promise<User> {
  try {
    const response = await fetch(`/api/users/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(dados),
    })
    
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `Erro HTTP: ${response.status}`)
    }
    
    const userData = await response.json()
    // Mapear dados do Supabase para o formato esperado
    const usuarioAtualizado = {
      id: userData.id,
      name: userData.name,
      email: userData.email,
      role: userData.role,
      active: userData.active,
      createdAt: userData.created_at
    }
    
    return usuarioAtualizado
  } catch (error) {
    console.error("❌ [USERS] Erro ao atualizar usuário:", error)
    throw error
  }
}

export async function deletarUsuario(id: string): Promise<void> {
  try {
    const response = await fetch(`/api/users/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    })
    
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `Erro HTTP: ${response.status}`)
    }
  } catch (error) {
    console.error("❌ [USERS] Erro ao deletar usuário:", error)
    throw error
  }
} 